import { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, ScrollText, Download } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_STYLE = {
  auto_pick_resolved: 'bg-indigo-100 text-indigo-700',
  manual_override:    'bg-amber-100 text-amber-800',
  manual_confirm:     'bg-emerald-100 text-emerald-700',
};

export default function RiskChoiceAuditPage() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ user_id: '', start: '', end: '' });
  const [downloading, setDownloading] = useState(false);

  const qs = () => {
    const p = new URLSearchParams();
    if (filters.user_id) p.set('user_id', filters.user_id);
    if (filters.start)   p.set('start', new Date(filters.start).toISOString());
    if (filters.end)     p.set('end',   new Date(filters.end).toISOString());
    return p.toString();
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/programs/riskchoice-audit?limit=500&${qs()}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) { setData({ error: 'Admin only' }); return; }
      setData(await r.json());
    } finally { setLoading(false); }
  };

  const downloadCsv = async () => {
    setDownloading(true);
    try {
      const r = await fetch(`${API}/programs/riskchoice-audit.csv?${qs()}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) return;
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = r.headers.get('content-disposition')?.match(/filename="([^"]+)"/)?.[1] || 'riskchoice-audit.csv';
      a.click();
      URL.revokeObjectURL(url);
    } finally { setDownloading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (loading) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  if (data?.error) return <div className="p-8 text-red-600">{data.error}</div>;

  const events = data.events || [];
  const summary = data.by_merchant || {};

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="riskchoice-audit-page">
      <div className="flex justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight flex items-center gap-2">
            <ScrollText className="w-6 h-6 text-indigo-500" /> RiskChoice Audit Trail
          </h1>
          <p className="text-slate-500 mt-1">Every AUTO pick, override and confirm — timestamped for dispute defense.</p>
        </div>
        <Button size="sm" onClick={downloadCsv} disabled={downloading} data-testid="download-csv-btn">
          {downloading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
          Download CSV
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Filters</CardTitle><CardDescription>Empty fields = all merchants / all time.</CardDescription></CardHeader>
        <CardContent className="grid sm:grid-cols-4 gap-3 items-end">
          <div>
            <Label>Merchant ID</Label>
            <Input value={filters.user_id} onChange={e => setFilters(f => ({ ...f, user_id: e.target.value }))} placeholder="full user id" data-testid="filter-user" />
          </div>
          <div>
            <Label>Start</Label>
            <Input type="date" value={filters.start} onChange={e => setFilters(f => ({ ...f, start: e.target.value }))} data-testid="filter-start" />
          </div>
          <div>
            <Label>End</Label>
            <Input type="date" value={filters.end} onChange={e => setFilters(f => ({ ...f, end: e.target.value }))} data-testid="filter-end" />
          </div>
          <Button onClick={load} data-testid="apply-filters-btn">Apply filters</Button>
        </CardContent>
      </Card>

      <div className="grid sm:grid-cols-3 gap-4">
        <Card><CardContent className="p-4">
          <div className="text-xs text-slate-500">Total events</div>
          <div className="text-2xl font-bold mt-1">{events.length}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-xs text-slate-500">Merchants active</div>
          <div className="text-2xl font-bold mt-1">{Object.keys(summary).length}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-xs text-slate-500">Overrides</div>
          <div className="text-2xl font-bold text-amber-600 mt-1">
            {events.filter(e => e.event === 'manual_override').length}
          </div>
        </CardContent></Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Per-Merchant Summary</CardTitle></CardHeader>
        <CardContent>
          {Object.keys(summary).length === 0 ? (
            <p className="text-sm text-slate-500 py-4 text-center">No merchant activity in this window.</p>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="text-slate-500 border-b">
                <th className="text-left py-2">Merchant</th>
                <th className="text-right">AUTO picks</th>
                <th className="text-right">Overrides</th>
                <th className="text-right">Confirms</th>
                <th className="text-right">Total</th>
                <th className="text-right"></th>
              </tr></thead>
              <tbody>
                {Object.entries(summary).sort((a, b) => b[1].total - a[1].total).map(([uid, s]) => (
                  <tr key={uid} className="border-b last:border-b-0">
                    <td className="py-2 font-mono text-xs">{uid.slice(0,8)}…</td>
                    <td className="text-right tabular-nums text-indigo-600">{s.auto_picks}</td>
                    <td className="text-right tabular-nums text-amber-600">{s.overrides}</td>
                    <td className="text-right tabular-nums text-emerald-600">{s.confirms}</td>
                    <td className="text-right tabular-nums font-semibold">{s.total}</td>
                    <td className="text-right">
                      <button className="text-xs text-indigo-600 hover:underline" onClick={() => { setFilters(f => ({ ...f, user_id: uid })); setTimeout(load, 100); }}>Filter</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Recent Events</CardTitle><CardDescription>Newest first · up to 500.</CardDescription></CardHeader>
        <CardContent>
          {events.length === 0 ? (
            <p className="text-sm text-slate-500 py-4 text-center">No events in this window.</p>
          ) : (
            <div className="space-y-1 text-sm max-h-[600px] overflow-auto">
              {events.map((e, i) => (
                <div key={i} className="flex gap-3 py-1.5 border-b last:border-b-0 items-center" data-testid={`audit-row-${i}`}>
                  <span className="text-slate-400 text-xs w-40 shrink-0 tabular-nums">{new Date(e.ts).toLocaleString()}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded font-mono shrink-0 ${EVENT_STYLE[e.event] || 'bg-slate-100 text-slate-700'}`}>{e.event}</span>
                  <span className="font-mono text-xs text-slate-500 w-24 shrink-0 truncate">{(e.user_id || '?').slice(0,8)}…</span>
                  <span className="font-mono text-xs w-20 shrink-0">
                    {e.event === 'manual_override' ? (
                      <><s className="text-slate-400">{e.auto_code || '—'}</s> → <b className="text-amber-700">{e.code}</b></>
                    ) : (
                      <b>{e.code || '—'}</b>
                    )}
                  </span>
                  <span className="text-xs text-slate-500 tabular-nums">
                    {e.declared_value && <>${e.declared_value.toLocaleString()} </>}
                    {e.rule && <>· rule: {e.rule}</>}
                    {e.carrier && <> · {e.carrier}</>}
                    {e.price !== null && e.price !== undefined && <> · ${Number(e.price).toFixed(2)}</>}
                    {e.reason && <> · <span className="text-red-600">{e.reason}</span></>}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
